import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Image, Pressable, StyleSheet, TextInput, View } from 'react-native'
import * as Clipboard from 'expo-clipboard'
import Toast from 'react-native-toast-message'
import { useRouter } from 'expo-router'
import { useConvex } from 'convex/react'
import { useDebounce } from 'use-debounce'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { JOIN_CODE_FORMAT_ERROR_CODE, JOIN_CODE_TAKEN_ERROR_CODE } from '@ppwr/convex/lounges/errors'
import {
  LOUNGE_JOIN_CODE_MAX_LENGTH,
  LOUNGE_JOIN_CODE_MIN_LENGTH,
  getLoungeJoinCodeFormatError,
  normalizeLoungeJoinCode
} from '@ppwr/convex/lounges/shared'
import { DS } from '@/design-system'
import { translate } from '../../../i18n'
import { getLoungeShareUrl } from '../../../utils/shareUrl'
import { isConvexErrorWithCode } from '../../../utils/errors/convex'

/**
 * LoungeCodeCard
 * ----------------------------------------------------------------------------
 * Unified presentational card for the lounge "join code" UX.
 *
 * Renders one of two visual variants of the same card shell:
 *
 *  - `mode="share"` — used in the lounge lobby (see `LoungePlayTab`). Shows
 *    the lounge's join code as a copyable read-only field, plus QR / rotate
 *    actions at the bottom.
 *  - `mode="join"` — used on the lounge list screen (see `LoungeListScreen`).
 *    Lets the user type a code into a `TextInput` and submit it via the
 *    inline arrow button to join an existing lounge.
 *
 * Both modes share the same rounded white card surface (with extra top
 * padding to reserve space for the decoration) and a centered title +
 * subtitle. The variants differ in three places:
 *
 *  1. Decoration above the card — illustrated suits/avatars cluster for
 *     share, vs. the promo avatar image for join.
 *  2. Code field — read-only display with a Copy icon for share, vs. an
 *     editable `TextInput` with a submit arrow for join.
 *  3. Footer — QR / rotate action row for share, vs. an inline error label
 *     for join.
 *
 * History: this component is the product of merging two previously
 * duplicated card UIs:
 *  - the legacy `LoungeShareCard` (share mode), and
 *  - the inline `JoinALoungeSection` that lived inside `LoungeListScreen`
 *    (join mode).
 *
 * The card encapsulates the self-contained share-code interactions (copying
 * the code to the clipboard and navigating to the QR screen) so every caller
 * gets identical behavior for free. Convex mutations (rotating the code,
 * joining a lounge) and screen-level concerns (modals such as
 * `LoungeFullModal`, controlled input state, submitting/error flags) remain
 * the parent's responsibility and are wired in via callbacks.
 * ----------------------------------------------------------------------------
 */

type CommonProps = {
  /** Optional override for the title; defaults to the per-mode i18n string. */
  title?: string
  /** Optional override for the subtitle; defaults to the per-mode i18n string. */
  subtitle?: string
}

export type LoungeCodeCardShareProps = CommonProps & {
  mode: 'share' | 'admin'
  /** Join code to display. `null` renders an em-dash placeholder. */
  joinCode: string | null
  onRotateCode?: () => void
  canRotate?: boolean
  rotating?: boolean
  /**
   * Enables the pencil-to-edit custom join code flow (owners only). When set, the
   * field becomes editable and Copy moves into the action row. Requires `loungeId`
   * for the live availability check. Should throw on failure so the card can show
   * the matching error.
   */
  onSaveCode?: (code: string) => Promise<void>
  loungeId?: Id<'lounges'>
}

export type LoungeCodeCardJoinProps = CommonProps & {
  mode: 'join'
  /** Controlled value of the join-code input. Should be upper-cased by the parent. */
  code: string
  onChangeCode: (next: string) => void
  onSubmit: () => void
  submitting?: boolean
  error?: string | null
}

export type LoungeCodeCardProps = LoungeCodeCardShareProps | LoungeCodeCardJoinProps

export function LoungeCodeCard(props: LoungeCodeCardProps) {
  const isShare = props.mode === 'share' || props.mode === 'admin'
  const isAdmin = props.mode === 'admin'
  const title = props.title ?? translate(isShare ? 'loungeLobbyScreen.shareLounge' : 'lounges.joinALoungeTitle')
  const subtitle =
    props.subtitle ?? translate(isShare ? 'loungeLobbyScreen.shareLoungeSubtitle' : 'lounges.joinALoungeDescription')

  return (
    <View style={styles.wrapper}>
      {!isAdmin && (
        <View
          style={[styles.decorationLayer, props.mode === 'join' && styles.decorationLayerJoin]}
          pointerEvents="none"
        >
          <JoinPromoImage />
        </View>
      )}

      <View style={[styles.card, isShare ? styles.cardShare : styles.cardJoin, isAdmin && styles.cardAdmin]}>
        {!isAdmin && (
          <View style={styles.titleBlock}>
            <DS.Text variant="Heading 2/Medium" textAlign="center" color="Brand/Maroon">
              {title}
            </DS.Text>
            <DS.Text variant="Body Medium/Medium" textAlign="center" color="Global/Black">
              {subtitle}
            </DS.Text>
          </View>
        )}

        {props.mode !== 'join' ? (
          <ShareCodeBody {...props} />
        ) : (
          <JoinCodeInput
            code={props.code}
            onChangeCode={props.onChangeCode}
            onSubmit={props.onSubmit}
            submitting={props.submitting ?? false}
            hasError={props.error != null}
          />
        )}

        {props.mode === 'join' && props.error != null && (
          <DS.Text variant="Caption/Regular" style={{ color: DS.palette['Utility/Error'] }}>
            {props.error}
          </DS.Text>
        )}
      </View>
    </View>
  )
}

// ----------------------------------------------------------------------------
// Share-mode internals
// ----------------------------------------------------------------------------

/** Share/admin card body: the owner-editable variant when `onSaveCode` is wired, else read-only. */
function ShareCodeBody({
  joinCode,
  onRotateCode,
  canRotate,
  rotating,
  onSaveCode,
  loungeId
}: LoungeCodeCardShareProps) {
  if (onSaveCode && loungeId) {
    return (
      <EditableJoinCode
        loungeId={loungeId}
        joinCode={joinCode}
        onSaveCode={onSaveCode}
        onRotateCode={onRotateCode}
        canRotate={canRotate}
        rotating={rotating}
      />
    )
  }
  return (
    <>
      <ShareCodeField joinCode={joinCode} />
      <ShareActionRow joinCode={joinCode} onRotateCode={onRotateCode} canRotate={canRotate} rotating={rotating} />
    </>
  )
}

function useCopyJoinCode(joinCode: string | null) {
  return useCallback(async () => {
    if (!joinCode) return
    await Clipboard.setStringAsync(joinCode)
    Toast.show({
      type: 'success',
      text1: translate('loungeLobbyScreen.copyCodeTitle'),
      text2: translate('loungeLobbyScreen.copyCodeSuccess')
    })
  }, [joinCode])
}

function useShowJoinCodeQR(joinCode: string | null) {
  const router = useRouter()
  return useCallback(() => {
    if (!joinCode) return
    const url = getLoungeShareUrl(joinCode)
    const bg = encodeURIComponent(DS.palette['Brand/Maroon'])
    const desc = encodeURIComponent('Scan to join this lounge on Poker Power.')
    router.push(`/lobby/qr-code?url=${encodeURIComponent(url)}&bg=${bg}&description=${desc}` as any)
  }, [joinCode, router])
}

function ShareCodeField({ joinCode }: { joinCode: string | null }) {
  const handleCopyCode = useCopyJoinCode(joinCode)

  return (
    <View style={styles.codeField}>
      <DS.Text variant="Mini/Bold" style={styles.codeFieldLabel} uppercase>
        {translate('loungeLobbyScreen.joinCode')}
      </DS.Text>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Copy join code"
        onPress={handleCopyCode}
        disabled={!joinCode}
        style={({ pressed }) => [styles.codeInput, pressed && joinCode ? { opacity: 0.7 } : null]}
      >
        <DS.Text variant="Body Medium/Medium" style={styles.codeText} numberOfLines={1}>
          {joinCode || '—'}
        </DS.Text>
        <DS.Icon name="Copy" size={20} color="Global/Black" />
      </Pressable>
    </View>
  )
}

function ShareActionRow({
  joinCode,
  onRotateCode,
  canRotate,
  rotating
}: Pick<LoungeCodeCardShareProps, 'joinCode' | 'onRotateCode' | 'canRotate' | 'rotating'>) {
  const handleShowQR = useShowJoinCodeQR(joinCode)

  return (
    <View style={styles.actionRow}>
      <Pressable
        onPress={handleShowQR}
        disabled={!joinCode}
        accessibilityRole="button"
        style={({ pressed }) => [styles.actionButton, pressed && joinCode ? { opacity: 0.7 } : null]}
      >
        <DS.Icon name="QR_code" size={20} color="Global/Black" />
        <DS.Text variant="Body Medium/Medium">{translate('loungeLobbyScreen.qrCode')}</DS.Text>
      </Pressable>
      {canRotate && onRotateCode && (
        <Pressable
          onPress={onRotateCode}
          disabled={rotating}
          accessibilityRole="button"
          style={({ pressed }) => [styles.actionButton, pressed && !rotating ? { opacity: 0.7 } : null]}
        >
          <DS.Icon name="Rotate_left" size={20} color="Global/Black" />
          <DS.Text variant="Body Medium/Medium">
            {rotating ? translate('loungeLobbyScreen.rotating') : translate('loungeLobbyScreen.rotateCode')}
          </DS.Text>
        </Pressable>
      )}
    </View>
  )
}

// ----------------------------------------------------------------------------
// Editable (owner) share-mode internals
// ----------------------------------------------------------------------------

type JoinCodeStatus =
  | 'idle'
  | 'checking'
  | 'available'
  | 'limit'
  | 'taken'
  | 'invalid_chars'
  | 'too_short'
  /** The live check failed (e.g. backend not deployed yet); saving is still allowed — the mutation re-validates. */
  | 'unverified'

type JoinCodeAvailability = { available: true } | { available: false; reason: string } | 'error'

/**
 * Debounced uniqueness check for the draft code. Uses an imperative query rather than `useQuery`
 * so a failing call (e.g. the function isn't deployed yet) degrades to `'error'` instead of
 * throwing during render and taking down the screen.
 */
function useJoinCodeAvailability(
  loungeId: Id<'lounges'>,
  joinCode: string,
  enabled: boolean
): JoinCodeAvailability | undefined {
  const convex = useConvex()
  const [result, setResult] = useState<{ code: string; availability: JoinCodeAvailability } | null>(null)

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    convex
      .query(api.lounges.queries.checkLoungeJoinCodeAvailability, { loungeId, joinCode })
      .then((availability) => {
        if (!cancelled) setResult({ code: joinCode, availability })
      })
      .catch((err) => {
        console.warn('checkLoungeJoinCodeAvailability failed', err)
        if (!cancelled) setResult({ code: joinCode, availability: 'error' })
      })
    return () => {
      cancelled = true
    }
  }, [convex, loungeId, joinCode, enabled])

  return enabled && result?.code === joinCode ? result.availability : undefined
}

/** Statuses that render the field in the error color. */
const ERROR_STATUSES: JoinCodeStatus[] = ['taken', 'invalid_chars', 'limit']

function deriveJoinCodeStatus({
  editing,
  isUnchanged,
  formatError,
  saveError,
  availability
}: {
  editing: boolean
  isUnchanged: boolean
  formatError: ReturnType<typeof getLoungeJoinCodeFormatError>
  saveError: 'taken' | 'invalid_chars' | null
  /** `undefined` while the debounced availability check is pending or skipped. */
  availability: JoinCodeAvailability | undefined
}): JoinCodeStatus {
  if (!editing || isUnchanged) return 'idle'
  if (formatError === 'invalid_chars') return 'invalid_chars'
  if (saveError) return saveError
  if (formatError === 'too_short') return 'too_short'
  if (availability === undefined) return 'checking'
  if (availability === 'error') return 'unverified'
  if (!availability.available) return availability.reason === 'taken' ? 'taken' : 'invalid_chars'
  return 'available'
}

/**
 * A 10-character code is valid, so the limit message only shows when the user tries to type past
 * it, and never over a real error. Saving is still governed by the underlying `status`.
 */
function toDisplayStatus(status: JoinCodeStatus, overflowAttempted: boolean): JoinCodeStatus {
  if (!overflowAttempted || status === 'taken' || status === 'invalid_chars') return status
  return 'limit'
}

function getJoinCodeHelper(status: JoinCodeStatus, draft: string): { text: string; color: string } | null {
  switch (status) {
    case 'available':
      return {
        text: translate('loungeLobbyScreen.joinCodeAvailable', { code: draft }),
        color: DS.palette['Utility/Success']
      }
    case 'taken':
      return { text: translate('loungeLobbyScreen.joinCodeTaken'), color: DS.palette['Utility/Error'] }
    case 'invalid_chars':
      return { text: translate('loungeLobbyScreen.joinCodeInvalidChars'), color: DS.palette['Utility/Error'] }
    case 'limit':
      return {
        text: translate('loungeLobbyScreen.joinCodeLimit', { max: LOUNGE_JOIN_CODE_MAX_LENGTH }),
        color: DS.palette['Utility/Error']
      }
    case 'too_short':
      return {
        text: translate('loungeLobbyScreen.joinCodeTooShort', { min: LOUNGE_JOIN_CODE_MIN_LENGTH }),
        color: DS.palette['Brand/Dark Grey']
      }
    default:
      return null
  }
}

function getJoinCodeBorderColor(status: JoinCodeStatus): string {
  if (ERROR_STATUSES.includes(status)) return DS.palette['Utility/Error']
  if (status === 'available') return DS.palette['Utility/Success']
  return DS.palette['Global/Black']
}

function EditableJoinCode({
  loungeId,
  joinCode,
  onSaveCode,
  onRotateCode,
  canRotate,
  rotating
}: Required<Pick<LoungeCodeCardShareProps, 'loungeId' | 'onSaveCode'>> &
  Pick<LoungeCodeCardShareProps, 'joinCode' | 'onRotateCode' | 'canRotate' | 'rotating'>) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(joinCode ?? '')
  const [saving, setSaving] = useState(false)
  // Submitting also blurs the input, so guard against committing twice in the same tick.
  const savingRef = useRef(false)
  const [saveError, setSaveError] = useState<'taken' | 'invalid_chars' | null>(null)
  // Set when the user tries to type past the max length; the extra characters are dropped.
  const [overflowAttempted, setOverflowAttempted] = useState(false)
  const [debouncedDraft] = useDebounce(draft, 300)

  // A rotation (or any external update) replaces the code under us — drop the draft.
  useEffect(() => {
    setEditing(false)
    setDraft(joinCode ?? '')
    setSaveError(null)
    setOverflowAttempted(false)
  }, [joinCode])

  const isUnchanged = draft === (joinCode ?? '')
  const formatError = getLoungeJoinCodeFormatError(draft)
  const shouldCheck = editing && !isUnchanged && !formatError && debouncedDraft === draft
  const availability = useJoinCodeAvailability(loungeId, draft, shouldCheck)

  const status = deriveJoinCodeStatus({
    editing,
    isUnchanged,
    formatError,
    saveError,
    availability: shouldCheck ? availability : undefined
  })

  const displayStatus = toDisplayStatus(status, overflowAttempted)
  const atMaxLength = draft.length >= LOUNGE_JOIN_CODE_MAX_LENGTH

  const canSave = status === 'available' || status === 'unverified'

  const handleChange = useCallback((text: string) => {
    setSaveError(null)
    setOverflowAttempted(text.length > LOUNGE_JOIN_CODE_MAX_LENGTH)
    setDraft(text.slice(0, LOUNGE_JOIN_CODE_MAX_LENGTH).toUpperCase())
  }, [])

  const handleStartEditing = useCallback(() => {
    setDraft(joinCode ?? '')
    setSaveError(null)
    setOverflowAttempted(false)
    setEditing(true)
  }, [joinCode])

  const handleCommit = useCallback(async () => {
    if (isUnchanged) {
      setEditing(false)
      return
    }
    if (!canSave || savingRef.current) return
    savingRef.current = true
    setSaving(true)
    try {
      await onSaveCode(normalizeLoungeJoinCode(draft))
      setEditing(false)
    } catch (err) {
      if (isConvexErrorWithCode(err, JOIN_CODE_TAKEN_ERROR_CODE)) setSaveError('taken')
      else if (isConvexErrorWithCode(err, JOIN_CODE_FORMAT_ERROR_CODE)) setSaveError('invalid_chars')
      else Toast.show({ type: 'error', text1: translate('loungeLobbyScreen.joinCodeSaveError') })
    } finally {
      savingRef.current = false
      setSaving(false)
    }
  }, [isUnchanged, canSave, onSaveCode, draft])

  const borderColor = getJoinCodeBorderColor(displayStatus)
  const helper = getJoinCodeHelper(displayStatus, draft)

  return (
    <>
      <View style={styles.codeField}>
        <View style={styles.codeLabelRow}>
          <DS.Text variant="Mini/Bold" style={styles.codeFieldLabel} uppercase>
            {translate('loungeLobbyScreen.joinCode')}
          </DS.Text>
          {editing && (
            <DS.Text
              variant="Mini/Bold"
              style={[styles.codeFieldLabel, atMaxLength && { color: DS.palette['Utility/Error'] }]}
            >
              {`${draft.length}/${LOUNGE_JOIN_CODE_MAX_LENGTH}`}
            </DS.Text>
          )}
        </View>

        <View style={[styles.codeInput, { borderColor }]}>
          {editing ? (
            <TextInput
              style={[DS.typography['Body Medium/Medium'], styles.codeText, styles.codeTextInput]}
              value={draft}
              onChangeText={handleChange}
              onSubmitEditing={handleCommit}
              onBlur={handleCommit}
              editable={!saving}
              autoFocus
              autoCapitalize="characters"
              autoCorrect={false}
              autoComplete="off"
              returnKeyType="done"
              accessibilityLabel={translate('loungeLobbyScreen.editJoinCodeAccessibilityLabel')}
            />
          ) : (
            <DS.Text variant="Body Medium/Medium" style={styles.codeText} numberOfLines={1}>
              {joinCode || '—'}
            </DS.Text>
          )}
          {!editing ? (
            <Pressable
              onPress={handleStartEditing}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel={translate('loungeLobbyScreen.editJoinCodeAccessibilityLabel')}
              style={({ pressed }) => (pressed ? { opacity: 0.7 } : null)}
            >
              <DS.Icon name="Edit" size={20} />
            </Pressable>
          ) : status === 'taken' || status === 'invalid_chars' ? (
            <DS.Icon name="Warning--enclosed" size={20} color="Global/Black" />
          ) : null}
        </View>

        {helper && (
          <DS.Text variant="Caption/Regular" style={{ color: helper.color }}>
            {helper.text}
          </DS.Text>
        )}
      </View>

      <EditableActionRow joinCode={joinCode} onRotateCode={onRotateCode} canRotate={canRotate} rotating={rotating} />
    </>
  )
}

/** Copy / QR Code / Rotate row shown under the editable join code field. */
function EditableActionRow({
  joinCode,
  onRotateCode,
  canRotate,
  rotating
}: Pick<LoungeCodeCardShareProps, 'joinCode' | 'onRotateCode' | 'canRotate' | 'rotating'>) {
  const handleCopyCode = useCopyJoinCode(joinCode)
  const handleShowQR = useShowJoinCodeQR(joinCode)

  return (
    <View style={styles.actionRow}>
      <Pressable
        onPress={handleCopyCode}
        disabled={!joinCode}
        accessibilityRole="button"
        accessibilityLabel={translate('loungeSettingsScreen.joinCode.copyAccessibilityLabel')}
        style={({ pressed }) => [styles.actionButton, pressed && joinCode ? { opacity: 0.7 } : null]}
      >
        <DS.Icon name="Copy" size={24} color="Global/Black" />
        <DS.Text variant="Body Medium/Medium">{translate('loungeLobbyScreen.copy')}</DS.Text>
      </Pressable>
      <Pressable
        onPress={handleShowQR}
        disabled={!joinCode}
        accessibilityRole="button"
        accessibilityLabel={translate('loungeSettingsScreen.joinCode.showQrAccessibilityLabel')}
        style={({ pressed }) => [styles.actionButton, pressed && joinCode ? { opacity: 0.7 } : null]}
      >
        <DS.Icon name="QR_code" size={24} color="Global/Black" />
        <DS.Text variant="Body Medium/Medium">{translate('loungeLobbyScreen.qrCode')}</DS.Text>
      </Pressable>
      {canRotate && onRotateCode && (
        <Pressable
          onPress={onRotateCode}
          disabled={rotating}
          accessibilityRole="button"
          accessibilityLabel={translate('loungeSettingsScreen.joinCode.rotateAccessibilityLabel')}
          style={({ pressed }) => [styles.actionButton, pressed && !rotating ? { opacity: 0.7 } : null]}
        >
          <DS.Icon name="Rotate_left" size={24} color="Global/Black" />
          <DS.Text variant="Body Medium/Medium">
            {rotating ? translate('loungeLobbyScreen.rotating') : translate('loungeLobbyScreen.rotate')}
          </DS.Text>
        </Pressable>
      )}
    </View>
  )
}

// ----------------------------------------------------------------------------
// Join-mode internals
// ----------------------------------------------------------------------------

function JoinCodeInput({
  code,
  onChangeCode,
  onSubmit,
  submitting,
  hasError
}: {
  code: string
  onChangeCode: (next: string) => void
  onSubmit: () => void
  submitting: boolean
  hasError: boolean
}) {
  const disabled = !code.trim() || submitting
  return (
    <View style={styles.joinInputWrapper}>
      <TextInput
        style={styles.joinInput}
        placeholder={translate('lounges.enterCode')}
        placeholderTextColor={`${DS.palette['Global/Black']}80`}
        value={code}
        onChangeText={(t) => onChangeCode(t.toUpperCase())}
        autoCapitalize="characters"
        autoCorrect={false}
        maxLength={10}
        onSubmitEditing={onSubmit}
        returnKeyType="go"
      />
      <Pressable
        style={[styles.joinArrowButton, disabled && !hasError && { opacity: 0.4 }]}
        onPress={onSubmit}
        disabled={disabled}
        hitSlop={8}
      >
        {hasError ? (
          <DS.Icon name="Warning--enclosed" color="Utility/Warning" size={20} />
        ) : (
          <DS.Icon name="Arrow---right" color="Global/Black" size={20} />
        )}
      </Pressable>
    </View>
  )
}

function JoinPromoImage() {
  return (
    <Image
      source={require('../../../assets/lounge-join-promo.png')}
      style={styles.joinPromoImage}
      resizeMode="contain"
    />
  )
}

// ----------------------------------------------------------------------------
// Styles
// ----------------------------------------------------------------------------

const CARD_PADDING = 16
const DECORATION_HEIGHT_SHARE = 56
const DECORATION_HEIGHT_JOIN = 64

const styles = StyleSheet.create({
  wrapper: {
    // Provides the positioning context for the absolute decoration layer.
  },
  decorationLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: DECORATION_HEIGHT_SHARE,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1
  },
  decorationLayerJoin: {
    top: -8,
    height: DECORATION_HEIGHT_JOIN
  },
  card: {
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 16,
    paddingTop: 64,
    paddingHorizontal: CARD_PADDING,
    paddingBottom: CARD_PADDING
  },
  cardShare: {
    gap: 16,
    alignItems: 'stretch',
    overflow: 'hidden'
  },
  cardAdmin: {
    paddingTop: CARD_PADDING
  },
  cardJoin: {
    gap: 16,
    alignItems: 'center'
  },

  titleBlock: {
    gap: 4
  },

  // Share-mode styles
  decorationsContainer: {
    flex: 1,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center'
  },
  avatarCluster: {
    width: 96,
    height: 48,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center'
  },
  avatarCircle: {
    width: 32,
    height: 32,
    borderRadius: 16
  },
  avatarLeft: {
    position: 'absolute',
    left: 4,
    top: 12
  },
  avatarRight: {
    position: 'absolute',
    right: 4,
    top: 12
  },
  avatarCenter: {
    position: 'absolute',
    left: 32,
    top: 0,
    width: 40,
    height: 40,
    borderRadius: 20
  },
  decorationDot: {
    position: 'absolute'
  },
  dotLeft: { left: '32%', top: 4 },
  dotTopRight: { right: '32%', top: 0 },
  dotBottomLeft: { left: '34%', bottom: 4 },
  dotBottomRight: { right: '34%', bottom: 4 },
  codeField: {
    gap: 8
  },
  codeFieldLabel: {
    color: DS.palette['Brand/Dark Grey'],
    letterSpacing: 0.5
  },
  codeInput: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 14,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: DS.palette['Global/Black']
  },
  codeLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  codeText: {
    flex: 1,
    letterSpacing: 1
  },
  codeTextInput: {
    padding: 0,
    color: DS.palette['Global/Black'],
    // The bordered wrapper is the focus indicator; drop the browser's default outline on web.
    outlineStyle: 'none' as 'solid'
  },
  actionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8
  },

  // Join-mode styles
  joinPromoImage: {
    width: 120,
    height: 64
  },
  joinInputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: DS.palette['Global/Black'],
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 14,
    width: '100%',
    gap: 8
  },
  joinInput: {
    flex: 1,
    fontSize: 16,
    fontWeight: '500',
    letterSpacing: -0.25,
    color: DS.palette['Global/Black'],
    padding: 0
  },
  joinArrowButton: {
    padding: 2
  }
})
